<template>
  <section class="base-template mt-5">
    <div class="wrapper base-template__wrapper">
      <div class="base-template__content">
        
        <div class="heading-container">
          <h1>{{ $t('cyan_update.title') }}</h1>
          <!-- {{ useUserStore().user }}
          {{ useSettingStore() }} -->
        </div>
        <div class="emotions-slider">
          <div class="emotions-slider__nav slider-nav">
            <div class="slider-nav__item slider-nav__item_prev" tabindex="0">
              <svg width="16" height="28" viewBox="0 0 16 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 26L2 14L14 2" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div class="slider-nav__item slider-nav__item_next" tabindex="0">
              <svg width="16" height="28" viewBox="0 0 16 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 26L14 14L2 2" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </div>

          <div class="emotions-slider__slider swiper">
            
            <div class="emotions-slider__wrapper swiper-wrapper">
              <div v-for="(slide) in cyanUpdates" :key="slide.id" class="emotions-slider__slide swiper-slide">
                <div class="emotions-slider-item">
                  <div class="emotions-slider-item__image">
                    <img :src="slide.image" :alt="slide.title_en" />
                  </div>
                  <div class="emotions-slider-item__content">
                    <div class="emotions-slider-item__info">
                      <h2 class="emotions-slider-item__title">
                        {{ settingStore.language == 'en' ? slide.title_en : slide.title_ar }}
                      </h2>
                      <div class="emotions-slider-item__text" 
                           v-html="settingStore.language == 'en' ? slide.description_en : slide.description_ar">
                      </div>
                    </div>
                    <div class="emotions-slider-item__footer">
                      <a class="emotions-slider-item__btn" href="#" @click.prevent>
                        <span class="emotions-slider-item__btn-text">View more</span>
                        <span class="emotions-slider-item__btn-icon"></span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="emotions-slider__pagination slider-pagination"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import axiosInstance from '@/plugins/axios';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useSettingStore } from '@/stores/settingStore';
// import { useUserStore } from '@/stores/userStore';

const settingStore = useSettingStore();
const { locale } = useI18n();
const cyanUpdates = ref([]);
const swiperInstance = ref(null);

const fetchCyanUpdates = async () => {
  try {
    const response = await axiosInstance.get('/app-categories');
    cyanUpdates.value = response.data.data;
    await nextTick();
    initializeSwiper();
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

const initializeSwiper = () => {
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
  }

  swiperInstance.value = new Swiper('.emotions-slider__slider', {
    modules: [Navigation, Pagination],
    loop: true,
    speed: 600,
    observer: true,
    watchOverflow: true,
    watchSlidesProgress: true,
    initialSlide: 0,
    rtl: locale.value === 'ar',
    navigation: {
      nextEl: '.slider-nav__item_next',
      prevEl: '.slider-nav__item_prev',
      disabledClass: 'disabled'
    },
    pagination: {
      el: '.slider-pagination',
      type: 'bullets',
      modifierClass: 'slider-pagination',
      bulletClass: 'slider-pagination__item',
      bulletActiveClass: 'active',
      clickable: true
    },
    breakpoints: {
      320: {
        slidesPerView: 1,
        spaceBetween: 15,
        centeredSlides: true
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 20,
        centeredSlides: false
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 30,
        centeredSlides: false
      }
    }
  });
};

watch(locale, () => {
  nextTick(() => {
    initializeSwiper();
  });
});

onMounted(() => {
  fetchCyanUpdates();
});

onBeforeUnmount(() => {
  if (swiperInstance.value && typeof swiperInstance.value.destroy === 'function') {
    swiperInstance.value.destroy(true, true);
    swiperInstance.value = null;
  }
});
</script>

<style scoped>
.base-template__wrapper {
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 98px;
}

.heading-container {
  position: relative;
  text-align: center;
  padding: 20px 0 40px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

h1 {
  font-size: clamp(2rem, 5vw, 3rem) !important;
  color: #1268B1;
  display: inline-block;
  position: relative;
  padding: 10px 20px;
  z-index: 2;
}

/* Adding Wavy Effect */
h1::before,
h1::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 60px;
  height: 20px;
  background: url("/public/assets/wave.svg") repeat-x;
  background-size: contain;
  animation: waveMove 2s linear infinite;
  transform: translateY(-50%);
}

h1::before {
  left: -80px;
}

h1::after {
  right: -80px;
  animation-direction: reverse;
}

/* Wavy Animation */
@keyframes waveMove {
  0% {
    transform: translateY(-50%) translateX(0);
  }
  50% {
    transform: translateY(-50%) translateX(10px);
  }
  100% {
    transform: translateY(-50%) translateX(0);
  }
}


.emotions-slider {
  --color-gray: #818181;
  --color-gray-dark: #1e1e1e;
  position: relative;
  width: 100%;
  margin: 0 auto;
}

.emotions-slider__slide {
  height: auto;
  padding: 5px; /* Add slight padding to prevent shadow cutoff */
}

.emotions-slider-item {
  --border-radius: 10px;
  width: 100%;
  background: var(--color-gray-dark);
  border-radius: var(--border-radius);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.emotions-slider-item:hover {
  transform: translateY(-5px);
}

.emotions-slider-item__image {
  width: 100%;
  aspect-ratio: 400 / 270;
  overflow: hidden;
}

.emotions-slider-item__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
}

.emotions-slider-item:hover .emotions-slider-item__image img {
  transform: scale(1.05);
}

.emotions-slider-item__content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  padding: 20px;
  flex: 1;
}

.emotions-slider-item__info {
  flex: 1;
}

.emotions-slider-item__title {
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 10px;
  color: #fff;
}

.emotions-slider-item__text {
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
}

/* Navigation */
.slider-nav {
  display: flex;
  justify-content: space-between;
  position: absolute;
  top: 50%;
  left: -50px !important;
  right: -50px !important;
  transform: translateY(-50%);
  z-index: 10;
  pointer-events: none;
}

.slider-nav__item {
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.3s ease;
}

.slider-nav__item.disabled {
  opacity: 0.5;
  cursor: default;
}

.slider-nav__item:not(.disabled):hover {
  background: rgba(0, 0, 0, 0.7);
}

/* Pagination */
.slider-pagination {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.slider-pagination__item {
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transition: all 0.3s ease;
}
.slider-nav__item_next {
  right: -70px !important;
}
.slider-pagination__item.active {
  width: 24px;
  background: #fff;
  border-radius: 4px;
}

/* View More Button */
.emotions-slider-item__btn {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.emotions-slider-item__btn:hover {
  opacity: 0.8;
}

.emotions-slider-item__btn-icon {
  width: 24px;
  height: 24px;
  position: relative;
  transition: transform 0.3s ease;
}

.emotions-slider-item__btn:hover .emotions-slider-item__btn-icon {
  transform: translateX(5px);
}

/* Responsive Styles */
@media screen and (max-width: 1400px) {
  .base-template__wrapper {
    padding: 0 60px;
  }
  
  .slider-nav {
    left: -40px;
    right: -40px;
  }
}

@media screen and (max-width: 1200px) {
  .base-template__wrapper {
    padding: 0 40px;
  }
  
  .slider-nav {
    left: -30px;
    right: -30px;
  }
  
  .slider-nav__item {
    width: 40px;
    height: 40px;
  }
}

@media screen and (max-width: 768px) {
  .base-template__wrapper {
    padding: 0 20px;
  }
  
  .slider-nav {
    display: none;
  }
  
  .emotions-slider-item__content {
    padding: 15px;
  }
  
  .emotions-slider-item__title {
    font-size: 18px;
  }
  
  .emotions-slider-item__text {
    font-size: 14px;
  }
}

@media screen and (max-width: 576px) {
  .base-template__wrapper {
    padding: 0 15px;
  }
}

/* RTL Support */
[dir="rtl"] .emotions-slider {
  direction: rtl;
}

[dir="rtl"] .slider-nav__item_prev svg,
[dir="rtl"] .slider-nav__item_next svg {
  transform: scaleX(-1);
}

[dir="rtl"] .emotions-slider-item__btn {
  flex-direction: row-reverse;
}

[dir="rtl"] .emotions-slider-item__btn:hover .emotions-slider-item__btn-icon {
  transform: translateX(-5px);
}
</style>